import { Close as CloseIcon } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { LoadingButton } from "@mui/lab";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Autocomplete,
  IconButton,
  Chip,
  MenuItem,
} from "@mui/material";
import { red } from "@mui/material/colors";
import { enqueueSnackbar } from "notistack";
import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../utils/io";
import CreateAddressModal from "./CreateAddressModal";
import RhfTextField from "components/form/RhfTextField";

const DEFAULT_SHIPPING_ADDRESS = `STEL LIFE INC
2401 Walnut St., STE102 ,
PHILADELPHIA PA 19103
United States`;

const SHIPPING_ADDRESS_OPTIONS = [
  {
    label: "STEL Office (Default)",
    value: DEFAULT_SHIPPING_ADDRESS,
    isDefault: true,
  },
];

const INITIAL_FORM_VALUES = {
  manufacturer: null,
  customer: null,
  selectedDevices: [],
  shippingAddress: "",
  poNumber: "",
  additionalInstructions: "",
  reference: "",
  description: "",
};

function RequiredLabel({ children }) {
  return (
    <Box
      sx={{
        fontWeight: "bold",
        display: "flex",
        marginBottom: "10px",
      }}
    >
      {children}
      <Box
        component="span"
        sx={{
          fontSize: "15px",
          fontStyle: "Italic",
          justifyContent: "center",
          textAlign: "center",
          color: red[500],
        }}
      >
        *
      </Box>
    </Box>
  );
}

RequiredLabel.propTypes = {
  children: PropTypes.node.isRequired,
};

function FormField({ children }) {
  return <Box sx={{ marginBottom: "15px" }}>{children}</Box>;
}

FormField.propTypes = {
  children: PropTypes.node.isRequired,
};

function DeviceListItem({
  device,
  quantity,
  index,
  onQuantityChange,
  onRemove,
  disabled,
}) {
  const [localQuantity, setLocalQuantity] = useState(quantity);

  useEffect(() => {
    setLocalQuantity(quantity);
  }, [quantity]);

  const handleChange = (e) => {
    setLocalQuantity(e.target.value);
  };

  const handleBlur = () => {
    const parsed = parseInt(localQuantity, 10);
    if (!parsed || parsed < 1) {
      setLocalQuantity(1);
      onQuantityChange(index, 1);
    } else {
      onQuantityChange(index, parsed);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 1.5,
        border: "1px solid #c4c4c4",
        borderRadius: "4px",
      }}
    >
      <Typography sx={{ flex: 1, fontSize: 14 }}>
        {device.display_name || device.name || `${device.make} ${device.model}`}
      </Typography>
      <TextField
        type="number"
        label="Quantity"
        value={localQuantity}
        onChange={handleChange}
        onBlur={handleBlur}
        variant="outlined"
        size="small"
        sx={{ width: 100 }}
        inputProps={{ min: 1 }}
        disabled={disabled}
      />
      <IconButton
        size="small"
        onClick={() => onRemove(index)}
        disabled={disabled}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

DeviceListItem.propTypes = {
  device: PropTypes.shape({
    display_name: PropTypes.string,
    name: PropTypes.string,
    make: PropTypes.string,
    model: PropTypes.string,
  }).isRequired,
  quantity: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  onQuantityChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

function CreateOrderModal({ open, onClose, onSuccess, isSalesOrder }) {
  const [loading, setLoading] = useState(false);
  const [manufacturers, setManufacturers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [isCreateAddressModalOpen, setIsCreateAddressModalOpen] =
    useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    trigger,
  } = useForm({
    defaultValues: INITIAL_FORM_VALUES,
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "selectedDevices",
  });

  const customerId = watch("customer")?.id;

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!isSalesOrder) {
          const manufacturersRes = await get("admin/suppliers");
          if (manufacturersRes.status === 200) {
            setManufacturers(manufacturersRes.data.suppliers || []);
          }
        }

        const customersRes = await get("/admin/organizations");
        if (customersRes.status === 200) {
          setCustomers(customersRes.data.organizations || []);
        }

        const devicesRes = await get("/device_models");
        if (devicesRes.status === 200) {
          setDevices(devicesRes.data.device_models || []);
        }
      } catch (error) {
        enqueueSnackbar("Failed to load form data", { variant: "error" });
      }
    };

    if (open) {
      fetchData();
    }
  }, [open, isSalesOrder]);

  const fetchShippingAddresses = useCallback(async () => {
    if (!customerId) return;
    const shippingAddressRes = await get("/admin/customer_addresses", {
      org_id: customerId,
    });
    if (shippingAddressRes.status === 200) {
      setShippingAddresses(shippingAddressRes.data.inventree_addresses || []);
    }
  }, [customerId]);

  useEffect(() => {
    if (open && customerId) {
      fetchShippingAddresses();
    }
  }, [open, customerId, fetchShippingAddresses]);

  const handleClose = () => {
    if (!loading) {
      reset();
      onClose();
    }
  };

  const handleDeviceAdd = (device) => {
    const selectedDevices = watch("selectedDevices");
    const isAlreadyAdded = selectedDevices.some(
      (d) => d.device?.id === device.id
    );

    if (!isAlreadyAdded) {
      append({ device, quantity: 1 });
      trigger("selectedDevices");
    }
  };

  const handleQuantityChange = (index, quantity) => {
    const selectedDevices = watch("selectedDevices");
    const parsedQuantity = parseInt(quantity, 10) || 0;

    update(index, {
      ...selectedDevices[index],
      quantity: parsedQuantity,
    });
  };

  const handleAddressCreated = async (newAddress) => {
    // Refresh the addresses list
    await fetchShippingAddresses();
    // Auto-select the newly created address
    if (newAddress?.pk) {
      // Use reset to update the shipping address field
      reset({
        ...watch(),
        shippingAddress: newAddress.pk,
      });
    }
  };

  const buildOrderData = (data) => {
    const baseData = {
      ...(data?.customer?.id && {
        customer_org_id: data.customer.id,
        customer_name: data.customer.name || data.customer.alias,
      }),
      devices: data.selectedDevices.map((d) => ({
        device_model_id: d.device.id,
        device_name: d.device.name || `${d.device.make} ${d.device.model}`,
        quantity: d.quantity,
      })),
    };
    if (isSalesOrder) {
      return {
        ...baseData,
        reference: data.reference,
        description: data.description,
        inventree_shipping_address_id: data.shippingAddress,
      };
    }
    return {
      ...baseData,
      supplier_id: data.manufacturer.id,
      supplier_name: data.manufacturer.name,
      supplier_email: data.manufacturer.email,
      additional_instructions: data.additionalInstructions,
      shipping_address: data.shippingAddress,
      po_number: data.poNumber,
    };
  };

  const onSubmit = async (data, skipEmail = false) => {
    setLoading(true);

    try {
      const orderData = buildOrderData(data);
      if (!isSalesOrder && skipEmail) {
        orderData.skip_email = true;
      }
      const endpoint = isSalesOrder
        ? "admin/sales_orders/create"
        : "admin/purchase_orders/create";
      const res = await post(endpoint, orderData);

      if (res.status === 200 || res.status === 201) {
        let successMessage = "Sales order created successfully";
        if (!isSalesOrder) {
          successMessage = skipEmail
            ? "Purchase order created successfully"
            : "Purchase order created and email sent successfully";
        }
        enqueueSnackbar(successMessage, {
          variant: "success",
        });
        handleClose();
        onSuccess?.();
      } else {
        enqueueSnackbar(
          `Failed to create ${isSalesOrder ? "sales" : "purchase"} order`,
          {
            variant: "error",
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.detail ||
          `Error creating ${isSalesOrder ? "sales" : "purchase"} order`,
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const validationRules = {
    manufacturer: {
      required: "Please select a manufacturer",
    },
    ...(isSalesOrder && {
      customer: {
        required: "Please select a customer",
      },
    }),
    selectedDevices: {
      validate: (value) => value.length > 0 || "Please add at least one device",
    },
    shippingAddress: {
      required: "Please enter a shipping address",
      validate: (value) =>
        value.trim() !== "" || "Please enter a shipping address",
    },
    poNumber: {
      required: "Please enter a PO number",
      validate: (value) => value.trim() !== "" || "Please enter a PO number",
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: "600px" },
      }}
    >
      <DialogTitle>
        {isSalesOrder ? "Create Sales Order" : "Create Purchase Order"}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={loading}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <form onSubmit={handleSubmit(onSubmit)}>
          {isSalesOrder && (
            <FormField>
              <Box
                sx={{
                  fontWeight: "bold",
                  display: "flex",
                  marginBottom: "10px",
                }}
              >
                Reference
              </Box>
              <Controller
                name="reference"
                control={control}
                rules={validationRules.reference}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    placeholder="Reference"
                    variant="outlined"
                    sx={{
                      width: "100%",
                      borderRadius: "4px",
                    }}
                  />
                )}
              />
            </FormField>
          )}

          {!isSalesOrder && (
            <FormField>
              <RequiredLabel>Manufacturer</RequiredLabel>
              <Controller
                name="manufacturer"
                control={control}
                rules={validationRules.manufacturer}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    options={manufacturers}
                    getOptionLabel={(option) => option.name || ""}
                    value={value}
                    onChange={(e, newValue) => onChange(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select manufacturer"
                        variant="outlined"
                        size="small"
                        error={!!errors.manufacturer}
                        helperText={errors.manufacturer?.message}
                        sx={{
                          width: "100%",
                          borderRadius: "4px",
                        }}
                      />
                    )}
                    disabled={loading}
                  />
                )}
              />
            </FormField>
          )}

          <FormField>
            <Box
              sx={{
                fontWeight: "bold",
                display: "flex",
                marginBottom: "10px",
              }}
            >
              Customer
              {isSalesOrder && (
                <Box
                  component="span"
                  sx={{
                    fontSize: "15px",
                    fontStyle: "Italic",
                    justifyContent: "center",
                    textAlign: "center",
                    color: red[500],
                  }}
                >
                  *
                </Box>
              )}
            </Box>
            <Controller
              name="customer"
              control={control}
              rules={validationRules.customer}
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  options={customers}
                  getOptionLabel={(option) => option.name || option.alias || ""}
                  value={value}
                  onChange={(e, newValue) => onChange(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select customer"
                      variant="outlined"
                      size="small"
                      error={!!errors.customer}
                      helperText={errors.customer?.message}
                      sx={{
                        width: "100%",
                        borderRadius: "4px",
                      }}
                    />
                  )}
                  disabled={loading}
                />
              )}
            />
          </FormField>

          <FormField>
            <RequiredLabel>Devices</RequiredLabel>
            <Controller
              name="selectedDevices"
              control={control}
              rules={validationRules.selectedDevices}
              render={() => (
                <Autocomplete
                  options={devices}
                  getOptionLabel={(option) =>
                    option.display_name ||
                    option.name ||
                    `${option.make} ${option.model}` ||
                    ""
                  }
                  onChange={(e, value) => {
                    if (value) handleDeviceAdd(value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search and add devices"
                      variant="outlined"
                      size="small"
                      error={!!errors.selectedDevices}
                      helperText={errors.selectedDevices?.message}
                      sx={{
                        width: "100%",
                        borderRadius: "4px",
                      }}
                    />
                  )}
                  disabled={loading}
                  value={null}
                />
              )}
            />

            <Box
              sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}
            >
              {fields.map((item, index) => (
                <DeviceListItem
                  key={item.id}
                  device={item.device}
                  quantity={item.quantity}
                  index={index}
                  onQuantityChange={handleQuantityChange}
                  onRemove={remove}
                  disabled={loading}
                />
              ))}
              {fields.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2 }}
                >
                  No devices added yet
                </Typography>
              )}
            </Box>
          </FormField>
          {isSalesOrder && (
            <FormField>
              <Box
                sx={{
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "10px",
                }}
              >
                <Box sx={{ display: "flex" }}>
                  Shipping Address
                  <Box
                    component="span"
                    sx={{
                      fontSize: "15px",
                      fontStyle: "Italic",
                      justifyContent: "center",
                      textAlign: "center",
                      color: red[500],
                    }}
                  >
                    *
                  </Box>
                </Box>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setIsCreateAddressModalOpen(true)}
                  disabled={!customerId || loading}
                  variant="outlined"
                >
                  New Address
                </Button>
              </Box>
              <RhfTextField
                name="shippingAddress"
                control={control}
                select
                label="Shipping Address"
                size="small"
                variant="outlined"
                sx={{ mr: 2, mb: 2, width: "100%" }}
              >
                {shippingAddresses.map((item) => {
                  const addressParts = [];

                  if (item.title) addressParts.push(item.title);
                  if (item.line1) addressParts.push(item.line1);
                  if (item.line2) addressParts.push(item.line2);

                  // Build city, state, zip line (e.g., "Philadelphia, PA 19103")
                  const cityStateZip = [
                    item.postal_city,
                    item.province,
                    item.postal_code,
                  ]
                    .filter(Boolean)
                    .join(" ");

                  if (cityStateZip) addressParts.push(cityStateZip);
                  if (item.country) addressParts.push(item.country);

                  const addressLabel = addressParts.join(", ");

                  return (
                    <MenuItem value={item.pk} key={item.pk}>
                      {addressLabel || "No address"}
                    </MenuItem>
                  );
                })}
              </RhfTextField>
            </FormField>
          )}
          {!isSalesOrder && (
            <>
              <FormField>
                <RequiredLabel>Shipping Address</RequiredLabel>
                <Controller
                  name="shippingAddress"
                  control={control}
                  rules={validationRules.shippingAddress}
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      freeSolo
                      options={SHIPPING_ADDRESS_OPTIONS}
                      getOptionLabel={(option) => {
                        if (typeof option === "string") return option;
                        return option.value || "";
                      }}
                      value={value}
                      onChange={(_, newValue) => {
                        const addressValue =
                          typeof newValue === "string"
                            ? newValue
                            : newValue?.value || "";
                        onChange(addressValue);
                      }}
                      onInputChange={(_, newValue) => {
                        onChange(newValue);
                      }}
                      renderOption={(props, option) => (
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        <li {...props} key={option.label}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            <Typography
                              sx={{
                                whiteSpace: "pre-line",
                                fontSize: "14px",
                              }}
                            >
                              {option.value}
                            </Typography>
                            {option.isDefault && (
                              <Chip
                                label="Default"
                                color="success"
                                size="small"
                                sx={{ ml: 2 }}
                              />
                            )}
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          multiline
                          rows={4}
                          placeholder="Select default or enter shipping address..."
                          variant="outlined"
                          size="small"
                          error={!!errors.shippingAddress}
                          helperText={errors.shippingAddress?.message}
                          sx={{
                            width: "100%",
                            borderRadius: "4px",
                          }}
                        />
                      )}
                      disabled={loading}
                    />
                  )}
                />
              </FormField>

              <FormField>
                <RequiredLabel>Customer PO Number</RequiredLabel>
                <Controller
                  name="poNumber"
                  control={control}
                  rules={validationRules.poNumber}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="e.g., PO-A011117064"
                      disabled={loading}
                      variant="outlined"
                      size="small"
                      error={!!errors.poNumber}
                      helperText={errors.poNumber?.message}
                      sx={{
                        width: "100%",
                        borderRadius: "4px",
                      }}
                    />
                  )}
                />
              </FormField>

              <FormField>
                <Box
                  sx={{
                    fontWeight: "bold",
                    display: "flex",
                    marginBottom: "10px",
                  }}
                >
                  Additional Instructions
                </Box>
                <Controller
                  name="additionalInstructions"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      multiline
                      rows={3}
                      placeholder="Shipping instructions, carrier information, etc."
                      disabled={loading}
                      variant="outlined"
                      size="small"
                      sx={{
                        width: "100%",
                        borderRadius: "4px",
                      }}
                    />
                  )}
                />
              </FormField>
            </>
          )}

          {isSalesOrder && (
            <FormField>
              <Box
                sx={{
                  fontWeight: "bold",
                  display: "flex",
                  marginBottom: "10px",
                }}
              >
                Description
              </Box>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    multiline
                    rows={3}
                    placeholder="Description"
                    disabled={loading}
                    variant="outlined"
                    size="small"
                    sx={{
                      width: "100%",
                      borderRadius: "4px",
                    }}
                  />
                )}
              />
            </FormField>
          )}
        </form>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        {!isSalesOrder && (
          <LoadingButton
            variant="outlined"
            onClick={handleSubmit((data) => onSubmit(data, true))}
            loading={loading}
            sx={{ mr: 1 }}
          >
            Create Order Only
          </LoadingButton>
        )}
        <LoadingButton
          variant="contained"
          onClick={handleSubmit((data) => onSubmit(data, false))}
          loading={loading}
        >
          {isSalesOrder
            ? "Create Order"
            : "Create Order and Contact Manufacturer"}
        </LoadingButton>
      </DialogActions>

      <CreateAddressModal
        open={isCreateAddressModalOpen}
        onClose={() => setIsCreateAddressModalOpen(false)}
        onSuccess={handleAddressCreated}
        orgId={customerId}
      />
    </Dialog>
  );
}

CreateOrderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  isSalesOrder: PropTypes.bool,
};

CreateOrderModal.defaultProps = {
  onSuccess: undefined,
  isSalesOrder: false,
};

export default CreateOrderModal;
